import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Wrench, 
  Clock, 
  User, 
  Calendar,
  DollarSign,
  Search,
  Plus,
  Play,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { workOrders, assets, getAssetById, getBuildingById } from '@/data/mockData';
import { getMergedWorkOrders, addWorkOrder, resolveFaultsByWorkOrderId } from '@/data/faultStore';
import { EmptyState } from '@/components/EmptyState';

const getStatusColor = (status) => {
  const colors = {
    open: 'default',
    inProgress: 'info',
    completed: 'success',
  };
  return colors[status] || 'default';
};

const getPriorityColor = (priority) => {
  const colors = {
    high: 'destructive',
    medium: 'warning',
    low: 'default',
  };
  return colors[priority] || 'default';
};

const WorkOrderCard = ({ workOrder, onStatusChange }) => {
  const { t } = useLanguage();
  const asset = getAssetById(workOrder.assetId);
  const building = asset ? getBuildingById(asset.buildingId) : null;
  const canStart = workOrder.status === 'open';
  const canComplete = workOrder.status === 'inProgress';

  return (
    <Card className="shadow-soft hover:shadow-elevated transition-all">
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-foreground text-base">
                  {workOrder.title}
                </h3>
                <Badge variant={getPriorityColor(workOrder.priority)}>
                  {t(workOrder.priority)}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                {workOrder.description}
              </p>
              <p className="text-xs text-muted-foreground">
                {asset?.name} • {building?.name}
              </p>
            </div>
            <Badge variant={getStatusColor(workOrder.status)} className="flex-shrink-0">
              {t(workOrder.status)}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">{t('assignedTo')}</p>
                <p className="text-sm font-medium text-foreground">{workOrder.assignedTo}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">{t('dueDate')}</p>
                <p className="text-sm font-medium text-foreground">
                  {new Date(workOrder.dueDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="flex gap-4">
              <div>
                <p className="text-xs text-muted-foreground">{t('opex')}</p>
                <p className="text-sm font-bold text-primary">
                  {(workOrder.opex ?? 0).toLocaleString()} Kč
                </p>
              </div>
              {(workOrder.capex ?? 0) > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground">{t('capex')}</p>
                  <p className="text-sm font-bold text-accent">
                    {workOrder.capex.toLocaleString()} Kč
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span className="text-sm">{workOrder.estimatedHours ?? 0}h</span>
            </div>
          </div>

          {(workOrder.createdBy || workOrder.createdAt) && (
            <p className="text-xs text-muted-foreground pt-2 border-t border-border">
              {t('createdBy')}: {workOrder.createdBy || '—'}{workOrder.createdAt ? `, ${new Date(workOrder.createdAt).toLocaleString()}` : ''}
            </p>
          )}

          {(canStart || canComplete) && onStatusChange && (
            <div className="flex gap-2 pt-4 border-t border-border">
              {canStart && (
                <Button
                  size="sm"
                  variant="default"
                  className="gap-2"
                  onClick={() => onStatusChange(workOrder.id, 'inProgress')}
                >
                  <Play className="w-4 h-4" />
                  {t('startWorkOrder')}
                </Button>
              )}
              {canComplete && (
                <Button
                  size="sm"
                  variant="default"
                  className="gap-2 bg-success hover:bg-success/90"
                  onClick={() => onStatusChange(workOrder.id, 'completed')}
                >
                  <CheckCircle className="w-4 h-4" />
                  {t('completeWorkOrder')}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const STATUS_OVERRIDES_KEY = 'fm-wo-statusOverrides';

const loadStatusOverrides = () => {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem(STATUS_OVERRIDES_KEY) : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const defaultWoForm = () => ({
  assetId: '',
  title: '',
  description: '',
  priority: 'medium',
  assignedTo: '',
  dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  opex: 0,
  capex: 0,
  estimatedHours: 0,
});

export const WorkOrdersPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [woForm, setWoForm] = useState(defaultWoForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusOverrides, setStatusOverrides] = useState(() => loadStatusOverrides());

  const allWorkOrders = getMergedWorkOrders(workOrders);
  const displayWorkOrders = allWorkOrders.map((wo) => ({
    ...wo,
    status: statusOverrides[wo.id] ?? wo.status,
  }));

  const handleStatusChange = (woId, newStatus) => {
    setStatusOverrides((prev) => {
      const next = { ...prev, [woId]: newStatus };
      try {
        if (typeof window !== 'undefined') localStorage.setItem(STATUS_OVERRIDES_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
    if (newStatus === 'completed') resolveFaultsByWorkOrderId(woId, user?.name ?? null);
    toast.success(newStatus === 'completed' ? t('toastWoCompleted') : t('toastWoStarted'));
  };

  const handleCreateWoSubmit = (e) => {
    e.preventDefault();
    if (!woForm.assetId || !woForm.title.trim()) {
      toast.error(t('toastWoValidationError'));
      return;
    }
    setIsSubmitting(true);
    const now = new Date().toISOString();
    addWorkOrder({
      assetId: woForm.assetId,
      title: woForm.title.trim(),
      description: (woForm.description || '').trim(),
      priority: woForm.priority,
      status: 'open',
      assignedTo: woForm.assignedTo.trim() || '—',
      createdDate: now.slice(0, 10),
      dueDate: woForm.dueDate,
      opex: Number(woForm.opex) || 0,
      capex: Number(woForm.capex) || 0,
      estimatedHours: Number(woForm.estimatedHours) || 0,
      createdBy: user?.name ?? null,
      createdAt: now,
    });
    toast.success(t('toastWoCreated'));
    setWoForm(defaultWoForm());
    setCreateDialogOpen(false);
    setIsSubmitting(false);
  };
  const filteredWorkOrders = displayWorkOrders.filter(wo => {
    const matchesSearch = (wo.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (wo.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || wo.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || wo.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const workOrdersByStatus = {
    open: displayWorkOrders.filter(wo => wo.status === 'open').length,
    inProgress: displayWorkOrders.filter(wo => wo.status === 'inProgress').length,
    completed: displayWorkOrders.filter(wo => wo.status === 'completed').length,
  };

  const totalOpex = displayWorkOrders.reduce((sum, wo) => sum + (wo.opex || 0), 0);
  const totalCapex = displayWorkOrders.reduce((sum, wo) => sum + (wo.capex || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">{t('workOrderList')}</h1>
          <p className="text-muted-foreground">
            {filteredWorkOrders.length} {t('workOrders').toLowerCase()}
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="w-4 h-4" />
              {t('createWorkOrder')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('createWorkOrder')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateWoSubmit} className="space-y-4">
              <div>
                <Label>{t('equipment')} / Asset</Label>
                <Select value={woForm.assetId} onValueChange={(v) => setWoForm((f) => ({ ...f, assetId: v }))}>
                  <SelectTrigger><SelectValue placeholder="Vyberte zařízení" /></SelectTrigger>
                  <SelectContent>
                    {assets.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Název</Label>
                <Input
                  value={woForm.title}
                  onChange={(e) => setWoForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Název zakázky"
                  required
                />
              </div>
              <div>
                <Label>{t('description')}</Label>
                <Textarea
                  value={woForm.description}
                  onChange={(e) => setWoForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Popis..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('priority')}</Label>
                  <Select value={woForm.priority} onValueChange={(v) => setWoForm((f) => ({ ...f, priority: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">{t('low')}</SelectItem>
                      <SelectItem value="medium">{t('medium')}</SelectItem>
                      <SelectItem value="high">{t('high')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('dueDate')}</Label>
                  <Input
                    type="date"
                    value={woForm.dueDate}
                    onChange={(e) => setWoForm((f) => ({ ...f, dueDate: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>{t('assignedTo')}</Label>
                <Input
                  value={woForm.assignedTo}
                  onChange={(e) => setWoForm((f) => ({ ...f, assignedTo: e.target.value }))}
                  placeholder="Jméno nebo tým"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>{t('opex')} (Kč)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={woForm.opex || ''}
                    onChange={(e) => setWoForm((f) => ({ ...f, opex: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>{t('capex')} (Kč)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={woForm.capex || ''}
                    onChange={(e) => setWoForm((f) => ({ ...f, capex: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Odhad (h)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={woForm.estimatedHours || ''}
                    onChange={(e) => setWoForm((f) => ({ ...f, estimatedHours: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Zrušit</Button>
                <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Vytvářím…' : 'Vytvořit'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('open')}</p>
                <p className="text-3xl font-bold text-foreground">{workOrdersByStatus.open}</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <Wrench className="w-6 h-6 text-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft border-info border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('inProgress')}</p>
                <p className="text-3xl font-bold text-foreground">{workOrdersByStatus.inProgress}</p>
              </div>
              <div className="p-3 bg-info-light rounded-lg">
                <Clock className="w-6 h-6 text-info" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('opex')} Total</p>
                <p className="text-2xl font-bold text-primary">{(totalOpex / 1000).toFixed(0)}k Kč</p>
              </div>
              <div className="p-3 bg-primary-light rounded-lg">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('capex')} Total</p>
                <p className="text-2xl font-bold text-accent">{(totalCapex / 1000).toFixed(0)}k Kč</p>
              </div>
              <div className="p-3 bg-success-light rounded-lg">
                <DollarSign className="w-6 h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="shadow-soft">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t('status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('status')} - All</SelectItem>
                <SelectItem value="open">{t('open')}</SelectItem>
                <SelectItem value="inProgress">{t('inProgress')}</SelectItem>
                <SelectItem value="completed">{t('completed')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger>
                <SelectValue placeholder={t('priority')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('priority')} - All</SelectItem>
                <SelectItem value="high">{t('high')}</SelectItem>
                <SelectItem value="medium">{t('medium')}</SelectItem>
                <SelectItem value="low">{t('low')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Work Orders List */}
      {filteredWorkOrders.length === 0 ? (
        <EmptyState
          icon={Wrench}
          title={t('noData')}
          description={filterStatus !== 'all' || filterPriority !== 'all' || searchQuery ? 'Zkuste upravit filtry nebo vyhledávání.' : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredWorkOrders.map((wo) => (
            <WorkOrderCard
              key={wo.id}
              workOrder={wo}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}
    </div>
  );
};
